<template>
  <div class="portal-page">
    <!-- Fundo rotativo (fixo) -->
    <div class="background">
      <div
        class="background-slide"
        :class="{ active: activeLayer === 0 }"
        :style="{ backgroundImage: `url(${layers[0]})` }"
      />
      <div
        class="background-slide"
        :class="{ active: activeLayer === 1 }"
        :style="{ backgroundImage: `url(${layers[1]})` }"
      />
    </div>

    <!-- Conteúdo principal -->
    <div class="portal-content">
      <section
        class="portal-hero"
        aria-labelledby="transparencia-title"
      >
        <h1
          id="transparencia-title"
          class="portal-title"
        >
          Portal da Transparência
        </h1>
        <p class="portal-description">Pesquise atos, documentos e informações disponibilizadas pelo FUNARPEN.</p>
      </section>

      <!-- Caixa de busca centralizada -->
      <div class="search-container">
        <div class="search-box">
          <div class="input-wrapper">
            <input
              type="text"
              v-model="search"
              placeholder="Digite o que deseja buscar..."
              @keydown.enter="buscar"
            />
            <button
              v-if="search"
              class="clear-btn"
              @click="search = ''"
              aria-label="Limpar"
            >
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>
          <button
            @click="buscar"
            class="search-btn"
            aria-label="Buscar"
          >
            <span class="material-symbols-outlined">search</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'

definePageMeta({
  middleware: 'manutencao',
  title: 'Portal da Transparência',
})

useHead({
  title: 'FUNARPEN - Portal da Transparência',
  meta: [{ name: 'description', content: 'FUNARPEN' }],
})

const router = useRouter()
const search = ref('')

function buscar() {
  if (search.value.trim()) {
    router.push({
      path: '/portal-transparencia/resultado-busca',
      query: { termo: search.value.trim() },
    })
  }
}

const images = [
  '/backgrounds/c1.jpg',
  '/backgrounds/c6.webp',
  '/backgrounds/c7.jpg',
  '/backgrounds/c2.jpg',
  '/backgrounds/c15.jpg',
  '/backgrounds/c16.jpg',
  '/backgrounds/c17.webp',
]

const layers = ref(['', ''])
const activeLayer = ref(0)

let queue = []
let intervalId = null

// Fisher–Yates
function shuffle(array) {
  const arr = [...array]
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}

function refillQueue() {
  queue = shuffle(images)
}

function nextImage() {
  if (queue.length === 0) refillQueue()

  const nextLayer = activeLayer.value === 0 ? 1 : 0
  const nextSrc = queue.shift()

  const img = new Image()
  img.src = nextSrc

  img.onload = () => {
    layers.value[nextLayer] = nextSrc
    activeLayer.value = nextLayer
  }
}

function startCarousel() {
  stopCarousel()
  refillQueue()

  const firstSrc = queue.shift()
  const img = new Image()
  img.src = firstSrc

  img.onload = () => {
    layers.value[0] = firstSrc
    layers.value[1] = firstSrc
    activeLayer.value = 0

    intervalId = setInterval(nextImage, 7000)
  }
}

function stopCarousel() {
  if (intervalId) clearInterval(intervalId)
}

// Pausa automática quando aba perde foco
function handleVisibility() {
  document.hidden ? stopCarousel() : startCarousel()
}

function preloadImages() {
  images.forEach((src) => {
    const img = new Image()
    img.src = src
  })
}

onMounted(() => {
  preloadImages()
  startCarousel()
  document.addEventListener('visibilitychange', handleVisibility)
})

onBeforeUnmount(() => {
  stopCarousel()
  document.removeEventListener('visibilitychange', handleVisibility)
})
</script>

<style scoped>
.portal-page {
  position: relative;
  isolation: isolate;
  min-height: calc(100vh - var(--funarpen-header-height));
  overflow: hidden;
  background: var(--funarpen-navy);
}

.background {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
}

.background-slide {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-color: #000;
  opacity: 0;
  transform: scale(1.03);
  will-change: opacity, transform;
  transition:
    opacity 2.8s cubic-bezier(0.33, 0, 0.1, 1),
    transform 16s linear;
}

.background-slide.active {
  opacity: 1;
  transform: scale(1.08);
  z-index: 1;
}

.background-slide::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(8, 47, 73, 0.8), rgba(8, 47, 73, 0.66)), radial-gradient(circle at 50% 16%, rgba(191, 152, 81, 0.2), transparent 26rem);
}

.background::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: url('/backgrounds/noise.png');
  opacity: 0.035;
}

.portal-content {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: min(100%, 980px);
  min-height: calc(100vh - var(--funarpen-header-height) - 132px);
  margin: 0 auto;
  padding: clamp(48px, 8vh, 96px) clamp(16px, 4vw, 40px);
}

.portal-hero {
  max-width: 780px;
  margin-bottom: 30px;
  color: #fff;
  text-align: center;
}

.portal-kicker {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 0 14px;
  padding: 7px 12px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: #f5e8cb;
  font-size: 0.78rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.portal-title {
  margin: 0;
  color: #fff;
  font-size: clamp(2.2rem, 5vw, 4.2rem);
  font-weight: 900;
  line-height: 1;
  letter-spacing: 0;
  text-align: center;
  text-shadow: 0 18px 52px rgba(0, 0, 0, 0.36);
}

.portal-description {
  max-width: 620px;
  margin: 18px auto 0;
  color: rgba(255, 255, 255, 0.84);
  font-size: 1.08rem;
  line-height: 1.7;
}

.search-container {
  width: 100%;
  max-width: 820px;
  margin: 0 auto;
}

.search-box {
  display: flex;
  align-items: center;
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.34);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.94);
  overflow: hidden;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.24);
  backdrop-filter: blur(14px);
}

.input-wrapper {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
}

.search-box input {
  width: 100%;
  padding: 18px 52px 18px 26px;
  border: none;
  outline: none;
  color: var(--funarpen-ink);
  background: transparent;
  font-size: 1.05rem;
  font-weight: 600;
}

.search-box input::placeholder {
  color: rgba(16, 35, 52, 0.58);
}

.clear-btn {
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
  border: none;
  background: transparent;
  color: var(--funarpen-muted);
  cursor: pointer;
}

.clear-btn:hover {
  color: var(--funarpen-ink);
}

.search-btn {
  display: flex;
  align-items: center;
  min-height: 58px;
  padding: 0 26px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--funarpen-blue), var(--funarpen-blue-2));
  color: #fff;
  cursor: pointer;
  transition:
    filter 0.25s ease,
    transform 0.25s ease;
}

.search-btn:hover {
  filter: brightness(1.08);
  transform: translateX(-2px);
}

.material-symbols-outlined {
  font-size: 24px;
}

@media (max-width: 768px) {
  .portal-content {
    min-height: auto;
    justify-content: flex-start;
    padding: 44px 16px 56px;
  }

  .portal-title {
    font-size: 2.1rem;
  }

  .portal-description {
    font-size: 0.98rem;
  }

  .search-box input {
    font-size: 0.96rem;
    padding: 15px 44px 15px 18px;
  }

  .search-btn {
    min-height: 52px;
    padding: 0 20px;
  }

  .background-slide::after {
    background: rgba(8, 47, 73, 0.76);
  }
}
</style>
